import type { ReactNode } from "react";
import { Icon } from "@/components/icons";

/**
 * Dark pill chip with an optional leading icon and a remove button — shared
 * shell for the selected-item lists in DataSourceCard, TagListCard,
 * LanguageCard and SearchMultiSelectWithTags. `removeLabel` feeds the
 * button's aria-label and can differ from `children` (e.g. DataSourceCard
 * shows a short name but announces the full source name). Omit `onRemove`
 * to render the chip without a remove button (e.g. a non-removable "All" chip).
 */
export function RemovableChip({
  icon,
  children,
  removeLabel,
  onRemove,
  className = "inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-sm bg-grey-950 px-2 py-1 text-small font-medium text-white",
}: {
  icon?: ReactNode;
  children: ReactNode;
  removeLabel: string;
  onRemove?: () => void;
  className?: string;
}) {
  return (
    <span className={className}>
      {icon && <span className="flex shrink-0 items-center">{icon}</span>}
      <span className="min-w-0 wrap-anywhere">{children}</span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${removeLabel}`}
          className="flex size-5 shrink-0 items-center justify-center text-white/80 hover:text-white"
        >
          <Icon name="close_small" size={20} />
        </button>
      )}
    </span>
  );
}
